import { useState } from 'react';
import {
    dateToMonthKey,
    getCurrentFinancialYearRange,
    getLastCompletedFinancialYearRange,
    getLastCompletedMonthKey,
    getTrailing12MonthsRange,
    monthKeyToEndDate,
    monthKeyToStartDate,
    periodHeaderToMonthKey,
    subtractMonthsFromMonthKey,
} from '../../../../utils/dateUtils';
import { BANK_RANGE_MAX_MONTHS, QUICK_RANGE_KEYS } from './constants';
import {
    QuickRangeKey,
    BankRangeState,
    BankDataEnvelope,
    EnvelopeAccountSummary,
    EnvelopeAccountOverview,
    QuickRangeStateArgs,
} from './types';

export type { BankDataEnvelope };

const EMPTY_MONTHS = { startMonth: '', endMonth: '' };

// The quick-range helpers return ISO dates while this hook works in "YYYY-MM" month keys, hence the slice.
const toMonthKey = (isoDate: string) => isoDate.slice(0, 7);

const applyRangeTo = (setMonths: QuickRangeStateArgs['setMonths'], range: { startDate: string; endDate: string }) =>
    setMonths({ startMonth: toMonthKey(range.startDate), endMonth: toMonthKey(range.endDate) });

/** Derives the org's bank-data envelope (floor/ceiling month) from a DEFAULT, unfiltered bank statement summary response; a range-filtered response would shrink it. Returns null when it can't be derived. */
export function deriveBankDataEnvelope(
    summaryData: EnvelopeAccountSummary[] | undefined,
    bankingOverview: EnvelopeAccountOverview[] | undefined,
): BankDataEnvelope | null {
    const floors = (summaryData || [])
        .map((account) => account.headers?.find((header) => header.key === 'period')?.name)
        .filter((name): name is string => !!name)
        .map(periodHeaderToMonthKey)
        .filter(Boolean);

    const ceilings = (bankingOverview || [])
        .map((account) => account.closingBalanceDate)
        .filter((date): date is string => !!date)
        .map(dateToMonthKey)
        .filter(Boolean);

    if (!floors.length || !ceilings.length) return null;

    // Union bounds across banks, not intersection — a bank with a short history must not narrow the range for banks with a longer one.
    return {
        floor: floors.reduce((min, key) => (key < min ? key : min)),
        ceiling: ceilings.reduce((max, key) => (key > max ? key : max)),
    };
}

/** Shared quick-range apply/match logic for the Banking "Time Period" control, used by both useBankDateRange and useDrillDownDateRange. */
export function createQuickRangeState({ startMonth, endMonth, setMonths }: QuickRangeStateArgs) {
    const hasBothMonths = !!startMonth && !!endMonth;

    // Derived by comparing months rather than stored, so a stored key can never drift out of sync with the actual range.
    const matchesRange = (range: { startDate: string; endDate: string }) =>
        hasBothMonths && startMonth === toMonthKey(range.startDate) && endMonth === toMonthKey(range.endDate);

    const activeQuickRange: QuickRangeKey | null = !hasBothMonths
        ? null
        : matchesRange(getLastCompletedFinancialYearRange())
        ? QUICK_RANGE_KEYS.LAST_FY
        : matchesRange(getTrailing12MonthsRange())
        ? QUICK_RANGE_KEYS.LAST_12M
        : matchesRange(getCurrentFinancialYearRange())
        ? QUICK_RANGE_KEYS.CURRENT_FY
        : null;

    return {
        selectLastFY: () => applyRangeTo(setMonths, getLastCompletedFinancialYearRange()),
        selectLast12M: () => applyRangeTo(setMonths, getTrailing12MonthsRange()),
        selectCurrentFY: () => applyRangeTo(setMonths, getCurrentFinancialYearRange()),
        matchesRange,
        activeQuickRange,
    };
}

/** Org-keyed month-range state shared by the Bank Statements and Cashflow From Banking sub-views; DERIVES the applied months during render (never via an effect) so an org switch resets to empty before any effect can fire a stale-org request. */
export function useBankDateRange(orgId: string, dataEnvelope?: BankDataEnvelope | null) {
    const [state, setState] = useState<BankRangeState>({ orgId, ...EMPTY_MONTHS });

    const { startMonth, endMonth } = state.orgId === orgId ? state : EMPTY_MONTHS;

    // Bound the pickers to the org's actual data envelope when known; fall back to the static 120-month bounds otherwise.
    const staticCeiling = getLastCompletedMonthKey();
    const ceiling = dataEnvelope?.ceiling || staticCeiling;
    // Floor must never widen the span past the 120-month UW cap, so it's the more recent of the envelope floor and `ceiling - 119 months`.
    const capFloor = subtractMonthsFromMonthKey(ceiling, BANK_RANGE_MAX_MONTHS - 1);
    const floor = dataEnvelope?.floor ? (dataEnvelope.floor > capFloor ? dataEnvelope.floor : capFloor) : capFloor;

    const selectStartMonth = (month: string) => setState({ orgId, startMonth: month, endMonth });
    const selectEndMonth = (month: string) => setState({ orgId, startMonth, endMonth: month });

    const { selectLastFY, selectLast12M, selectCurrentFY, activeQuickRange } = createQuickRangeState({
        startMonth,
        endMonth,
        setMonths: (months) => setState({ orgId, ...months }),
    });

    // Reset means "show everything" — the next fetch sends no date keys at all.
    const clear = () => setState({ orgId, ...EMPTY_MONTHS });

    // Both months required — monthKeyToStartDate/EndDate('') parse to a bogus year-2001
    // date (see dateUtils comment), not an empty range, so a one-sided pick must not apply.
    const hasBothMonths = !!startMonth && !!endMonth;
    const applied = {
        startDate: hasBothMonths ? monthKeyToStartDate(startMonth) : '',
        endDate: hasBothMonths ? monthKeyToEndDate(endMonth) : '',
    };

    return {
        startMonth,
        endMonth,
        selectLastFY,
        selectLast12M,
        selectCurrentFY,
        activeQuickRange,
        // Start picker: floor at the 120-month cap, ceiling at the currently chosen end month.
        startPickerMin: floor,
        startPickerMax: endMonth || ceiling,
        // End picker: floor at the currently chosen start month, ceiling at last completed month.
        endPickerMin: startMonth || floor,
        endPickerMax: ceiling,
        selectStartMonth,
        selectEndMonth,
        clear,
        applied,
        rangeApplied: hasBothMonths,
    };
}
